/**
 * **Where one attention reason's WORDS live** — one decision, taken once, for all fifteen.
 *
 * Ten reasons are named by `clients.tasks.reasons.<reason>.*`, which is the mobile app's verbatim
 * bundle: a trainer moving between the phone and the desk reads one vocabulary, and a re-sync of
 * that bundle carries both apps forward together.
 *
 * Five are not. `PlanCompleted`, `NewClip` and `ClipCommentAwaitingReply` are live catalogue reasons
 * the phone has no copy for, and `src/shared/locales/{pl,en}.json` is never edited here — so their
 * words are this app's own, under `web:home.task.<reason>.*`, which is where new web copy belongs.
 *
 * `LapsedActivity` and `NoUpcomingSessions` moved there on 16 September 2026, at the owner's
 * instruction, because mobile's sentences say the wrong thing on this screen:
 *
 *   · "Brak ukończonego treningu od ponad 2 tygodni" names a window R21 moved to SEVEN days, and
 *     its `detail` — "Klient nie trenował od dłuższego czasu" — names no number at all. The web
 *     sentence states the actual count of days, which the roster already knows (`lastWorkoutAt`).
 *   · "Brak nadchodzących sesji" calls a workout a session; the owner's vocabulary on this surface
 *     is "trening", and the title follows the sentence so the two agree.
 *
 * Seven more followed the same day, for one structural reason: **mobile draws two lines and this
 * app draws one.** The phone puts the reason's TITLE above its sentence — "Oczekujące zaproszenie"
 * over "Wysłane 9 dni temu" — so mobile's `sub` is the second half of a sentence whose first half
 * is the title. The home's row replaces that title with the CLIENT'S NAME, and "Wysłane 9 dni temu"
 * under "QaCezary" never says WHAT was sent. `MissedWorkout`, `AbandonedWorkout`, `PlanEndingSoon`,
 * `PlanCompletionDue`, `InvitationPending`, `GoalRenewalDue` and `SessionAwaitingReview` therefore
 * read from `web:`, where each one is a whole sentence that names the EVENT.
 *
 * Every reason keeps every leaf mobile has, so no surface loses a string by the move.
 *
 * ── THE RULE EVERY SENTENCE UNDER `web:home.task` OBEYS ───────────────────────────────────────
 * **Genderless, always.** A sentence here is printed under a client's name and this app does not
 * know that client's gender — the wire carries none. "Nie przyjął", "Opuścił", "Zaczęła" are
 * therefore all wrong, and the first variant somebody adds without reading this will be one of
 * them. Word the event in the passive, as a verbal noun, or impersonally: "Zaproszenie bez
 * odpowiedzi od 9 dni", "Trening opuszczony 14 września", "Trening zaczęty i nieukończony".
 *
 * Two more constraints these sentences live under:
 *   · **It must read alone**, because nothing stands above it but a name. A fragment that leans on
 *     a title is the defect this whole move exists to fix.
 *   · **It must fit two lines in a ~159px column.** Short. The longest here is 43 characters
 *     ("Zaproszenie bez odpowiedzi od 999 dni" plus its widest count), measured 16 September 2026.
 *     The name of the plan, session or goal is dropped wherever it does not carry the event —
 *     the owner's instruction, and the room it frees is what lets the event fit.
 *
 * ── Why this is a module and not a template literal at four call sites ───────────────────────
 * Before it, `clients.tasks.reasons.${reason}.title` was written out in the roster table, the roster
 * preview panel, the home's muted footer and the home's own projection. Adding a reason whose copy
 * lives somewhere else meant finding all four and remembering which; missing one renders a raw
 * locale key at a paying trainer, and nothing in the type system would have said so.
 *
 * The table is a `Record` over the union on purpose: the next reason the backend adds does not
 * compile until somebody has said which bundle names it.
 */

import type { AttentionReason } from './clientRow';

/** The mobile bundle, copied here verbatim and never edited. */
const MOBILE = 'clients.tasks.reasons';

/** This app's own namespace, for the reasons mobile has no words for. */
const WEB = 'web:home.task';

const SCOPE: Readonly<Record<AttentionReason, string>> = {
  missedWorkout: WEB,
  abandonedWorkout: WEB,
  invitationPending: WEB,
  goalRenewalDue: WEB,
  planEndingSoon: WEB,
  planCompletionDue: WEB,
  // Mobile's sentence is the client's own message in quotation marks. That IS the event — a quote
  // under a name reads as "they said this" — so it needs no rewording and stays shared.
  unreadMessage: MOBILE,
  noUpcomingSessions: WEB,
  lapsedActivity: WEB,
  sessionAwaitingReview: WEB,
  // "Check-in tygodniowy · przesłano 15 września" names the event impersonally and names WHICH
  // survey, which the trainer needs. Shared.
  surveyAwaitingReview: MOBILE,
  // `Win`, so the home never draws a row for it; only the roster labels it, where the title is all
  // that is read.
  goalAchieved: MOBILE,
  planCompleted: WEB,
  newClip: WEB,
  clipCommentAwaitingReply: WEB,
  // A web-only reason with web-only copy, kept beside the rest of the public form's words.
  publicFormLeadSubmitted: 'web:publicForm.leads.attention',
  // The first generation (R26). Their sentences name an average, a count of readings and the
  // trainer's own level, or a record's exercise — words this app draws on its own cards.
  lowSleep: WEB,
  lowEnergy: WEB,
  prAchieved: WEB,
};

/**
 * One of a reason's keys, whole.
 *
 * `part` is the leaf — `title`, or one of the `sub` / `subEmpty` / `subNoWhen` / `detail` sentences
 * the home's row picks among. The scope stays inside this module: a caller that was handed the
 * prefix went on to template `${scope}.${reason}.${part}` itself, which is the same three-part
 * string assembled in two places and the thing this module exists to stop.
 */
export function attentionReasonKey(reason: AttentionReason, part: string): string {
  return `${SCOPE[reason]}.${reason}.${part}`;
}

/** The reason's own name, which is what every surface that labels one asks for. */
export function attentionReasonTitleKey(reason: AttentionReason): string {
  return attentionReasonKey(reason, 'title');
}
